import React, { useState } from 'react';
import { useParams, useLocation, Routes, Route, Navigate, Link } from 'react-router-dom';
import {
    FaBalanceScale,
    FaCamera,
    FaChevronLeft,
    FaChevronRight,
    FaDatabase,
    FaHdd,
    FaKey,
    FaLayerGroup,
    FaNetworkWired,
    FaProjectDiagram,
    FaServer,
    FaShieldAlt,
    FaTachometerAlt,
} from 'react-icons/fa';
import Overview from "./OverviewPage";
import Instances from "./InstancesPage";
import SecurityGroupsPage from './SecurityGroupsPage';
import SecurityGroupRules from "./SecurityGroupRules";
import SSHPage from "./SSHPage";
import PrivateNetworksPage from "./PrivateNetworksPage";
import VolumesPage from "./VolumesPage";
import SnapshotsPage from "./SnapshotsPage";
import BackupsPage from "./BackupsPage";
import LoadBalancersPage from "./LoadBalancersPage";
import ClustersPage from "./ClustersPage";
// reduOS retired 2026-07-14 (not managed for now). ReduceOsPage.tsx is kept. To re-add: restore this
// import, add 'reduOS' to the Compute subcategory list + the linkTo check, re-add its <Route> below,
// and add the nav item + handler in Navbar.tsx.
import DatabasesPage from './DatabasesPage';
import RelationalDbPage from './RelationalDbPage';
import ClickHousePage from './ClickHousePage';
import RedisPage from './RedisPage';
import QdrantPage from './QdrantPage';
import '../styles/CategoryPage.scss';

const categories = {
    Compute: ['Overview', 'Instances', 'SSH Keypairs', 'Snapshots', 'Backups', 'Clusters'],
    Network: ['Security Groups', 'Private Networks', 'Load Balancers'],
    'Block Storage': ['Volumes'],
    'Managed': ['Databases', 'MySQL', 'ClickHouse', 'Redis', 'Vector DB'],
};

const subsectionIcons: Record<string, React.ComponentType> = {
    Overview: FaTachometerAlt,
    Instances: FaServer,
    'SSH Keypairs': FaKey,
    Snapshots: FaCamera,
    Backups: FaDatabase,
    Clusters: FaLayerGroup,
    'Security Groups': FaShieldAlt,
    'Private Networks': FaNetworkWired,
    'Load Balancers': FaBalanceScale,
    Volumes: FaHdd,
    Databases: FaDatabase,
    MySQL: FaDatabase,
    ClickHouse: FaProjectDiagram,
    Redis: FaDatabase,
    'Vector DB': FaLayerGroup,
};

const subsectionLabels: Record<string, string> = {
    Databases: 'PostgreSQL',
};

const transformCategoryName = (category: string): string => {
    return category
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const CategoryPage: React.FC = () => {
    let { category } = useParams<{ category: string }>();
    const location = useLocation();
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
    category = category ? transformCategoryName(category) : '';

    const categoryName = category || 'Compute';
    const subcategories = categories[categoryName as keyof typeof categories] || [];

    const toRoutePath = (name: string): string => {
        return name.toLowerCase().replace(/\s+/g, '-');
    };

    return (
        <div className={`category-page${isSidebarCollapsed ? ' category-page--collapsed' : ''}`}>
            <aside className="category-sidebar" aria-label={`${categoryName} navigation`}>
                <div className="category-sidebar__heading">
                    <span className="category-sidebar__eyebrow">{categoryName}</span>
                    <button
                        type="button"
                        className="category-sidebar__toggle"
                        onClick={() => setSidebarCollapsed(collapsed => !collapsed)}
                        aria-label={isSidebarCollapsed ? `Expand ${categoryName} navigation` : `Collapse ${categoryName} navigation`}
                        title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {isSidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                    </button>
                </div>
                <nav className="category-sidebar__nav">
                    {subcategories.map((sub) => {
                        const routePath = toRoutePath(sub);
                        const to = `/category/${categoryName.toLowerCase().replace(/\s+/g, '-')}/${routePath}`;
                        const active = location.pathname === to;
                        const SubsectionIcon = subsectionIcons[sub] || FaLayerGroup;
                        const label = subsectionLabels[sub] || sub;
                        return (
                            <Link key={sub} to={to} className={`category-sidebar__item${active ? ' is-active' : ''}`} title={label} aria-label={label} aria-current={active ? 'page' : undefined}>
                                {React.createElement(SubsectionIcon)}
                                <span>{label}</span>
                            </Link>
                        );
                    })}
                </nav>
            </aside>
            <main className="category-page__content">
                <Routes>
                    {subcategories.map((sub) => {
                        const routePath = toRoutePath(sub);
                        if (sub === 'Overview') {
                            return <Route key={sub} path={routePath} element={<Overview />} />;
                        }
                        if (sub === 'Instances') {
                            return <Route key={sub} path={routePath} element={<Instances />} />;
                        }
                        if (sub === 'Security Groups') {
                            return <Route key={sub} path={routePath} element={<SecurityGroupsPage />} />;
                        }
                        if (sub === 'Security rules') {
                            return <Route key={sub} path={routePath} element={<SecurityGroupRules />} />;
                        }
                        if (sub === 'SSH Keypairs') {
                            return <Route key={sub} path={routePath} element={<SSHPage/>}/>;
                        }
                        if (sub === 'Private Networks') {
                            return <Route key={sub} path={routePath} element={<PrivateNetworksPage />} />;
                        }
                        if (sub === 'Volumes') {
                            return <Route key={sub} path={routePath} element={<VolumesPage />} />;
                        }
                        if (sub === 'Snapshots') {
                            return <Route key={sub} path={routePath} element={<SnapshotsPage />} />;
                        }
                        if (sub === 'Backups') {
                            return <Route key={sub} path={routePath} element={<BackupsPage />} />;
                        }
                        if (sub === 'Load Balancers') {
                            return <Route key={sub} path={routePath} element={<LoadBalancersPage />} />;
                        }
                        if (sub === 'Clusters') {
                            return <Route key={sub} path={routePath} element={<ClustersPage />} />;
                        }
                        if (sub === 'Redis') {
                            return <Route key={sub} path={routePath} element={<RedisPage />} />;
                        }
                        if (sub === 'MySQL') {
                            return <Route key={sub} path={routePath} element={<RelationalDbPage />} />;
                        }
                        if (sub === 'ClickHouse') {
                            return <Route key={sub} path={routePath} element={<ClickHousePage />} />;
                        }
                        if (sub === 'Databases') {
                            return <Route key={sub} path={routePath} element={<DatabasesPage />} />;
                        }
                        if (sub === 'Vector DB') {
                            return <Route key={sub} path={routePath} element={<QdrantPage />} />;
                        }
                        return null;
                    })}
                    <Route path="/coming-soon" element={<div>Coming Soon</div>} />
                    <Route path="*" element={<Navigate to="/category/compute/overview" />} />
                </Routes>
            </main>
        </div>
    );
};

export default CategoryPage;
